import  { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import api from '../utils/axios';

const Dashboard = () => {
  const [chatOverview, setChatOverview] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [feedbackOverview, setFeedbackOverview] = useState(null);
  const [errorStats, setErrorStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    interval: 'day'
  });

  // Function to map agent IDs to friendly names
  const getAgentName = (agentId) => {
    // You can expand this mapping based on your actual agent IDs
    const agentNames = {
      'agent-001': 'Agent Pharma',
      'agent-002': 'Agent Support',
      'agent-003': 'Agent Sales',
      'agent-004': 'Agent Technical',
      'agent-005': 'Agent Billing',
      '3': 'Agent Pharma', // Specific mapping for "3" as requested
      // Add more mappings as needed
    };
    
    return agentNames[agentId] || `Agent ${agentId}`;
  };

  useEffect(() => {
    const fetchChatOverview = async () => {
      try {
        const response = await api.get('/api/analytics/chat-overview');
        setChatOverview(response.data);
      } catch (err) {
        setError('Failed to fetch chat overview data');
        console.error('Error fetching chat overview:', err);
      }
    };

    const fetchUserActivity = async () => {
      try {
        const params = {};
        if (dateRange.startDate) params.startDate = dateRange.startDate;
        if (dateRange.endDate) params.endDate = dateRange.endDate;
        params.interval = dateRange.interval;
        
        const response = await api.get('/api/analytics/user-activity', { params });
        setUserActivity(response.data);
      } catch (err) {
        setError('Failed to fetch user activity data');
        console.error('Error fetching user activity:', err);
      }
    };

    const fetchFeedbackOverview = async () => {
      try {
        const response = await api.get('/api/analytics/feedback-overview');
        setFeedbackOverview(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch feedback overview data');
        console.error('Error fetching feedback overview:', err);
        setLoading(false);
      }
    };

    const fetchErrorStats = async () => {
      try {
        const response = await api.get('/api/errors/stats/overview');
        setErrorStats(response.data);
      } catch (err) {
        // Error stats are not critical, so we don't set error state here
        console.error('Error fetching error stats:', err);
      }
    };

    fetchChatOverview();
    fetchUserActivity();
    fetchFeedbackOverview();
    fetchErrorStats();
  }, [dateRange]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilter = () => {
    // The useEffect will automatically trigger when dateRange changes
  };

  const handleResetFilter = () => {
    setDateRange({
      startDate: '',
      endDate: '',
      interval: 'day'
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Format interval for display
  const formatInterval = (interval) => {
    switch (interval) {
      case 'day': return 'Daily';
      case 'week': return 'Weekly';
      case 'month': return 'Monthly';
      default: return interval;
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 overflow-auto">
          <header className="bg-white border-b border-gray-200 px-6 py-4">
            <h1 className="text-2xl font-semibold text-black">Dashboard</h1>
            <p className="text-gray-600 mt-1">Monitor your application performance and user engagement</p>
          </header>
          <main className="p-6">
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
              <span className="ml-3 text-gray-600">Loading dashboard data...</span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 overflow-auto">
          <header className="bg-white border-b border-gray-200 px-6 py-4">
            <h1 className="text-2xl font-semibold text-black">Dashboard</h1>
            <p className="text-gray-600 mt-1">Monitor your application performance and user engagement</p>
          </header>
          <main className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-red-800">Error Loading Data</h3>
              </div>
              <p className="mt-2 text-red-700">{error}</p>
              <button 
                onClick={() => { setLoading(true); setError(null); setDateRange({...dateRange}); }}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-semibold text-black">Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor your application performance and user engagement</p>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Stats Overview */}
          {chatOverview && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
              {/* Total Messages */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9 8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Messages</p>
                    <p className="text-2xl font-bold text-gray-900">{chatOverview.totalMessages.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Sessions */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Sessions</p>
                    <p className="text-2xl font-bold text-gray-900">{chatOverview.sessionCount.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Users */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Active Users</p>
                    <p className="text-2xl font-bold text-gray-900">{chatOverview.userCount.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Avg Response Time */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-yellow-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg Response Time</p>
                    <p className="text-2xl font-bold text-gray-900">{chatOverview.responseTime.average}s</p>
                  </div>
                </div>
              </div>

              {/* Likes */}
              {chatOverview.feedback && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="p-3 bg-green-100 rounded-lg mr-4">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Likes</p>
                      <p className="text-2xl font-bold text-gray-900">{chatOverview.feedback.likes.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Dislikes */}
              {chatOverview.feedback && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="p-3 bg-red-100 rounded-lg mr-4">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018c.163 0 .326.02.485.06L17 4m0 0v9m0-9h2.765a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.264 15H11" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Dislikes</p>
                      <p className="text-2xl font-bold text-gray-900">{chatOverview.feedback.dislikes.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Stats Section */}
          {errorStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Total Errors */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-red-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Errors</p>
                    <p className="text-2xl font-bold text-gray-900">{errorStats.totalErrors.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Recent Errors */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-orange-100 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Recent Errors (30 days)</p>
                    <p className="text-2xl font-bold text-gray-900">{errorStats.recentErrors.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Error Types */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div>
                  <p className="text-sm text-gray-500">Error Types</p>
                  <p className="text-2xl font-bold text-gray-900">{errorStats.errorsByType.length}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {errorStats.errorsByType.slice(0, 2).map(type => type._id).join(', ')}
                    {errorStats.errorsByType.length > 2 ? '...' : ''}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Mode Distribution */}
            {chatOverview && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Message Mode Distribution</h3>
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="flex justify-center space-x-8 mb-4">
                      <div className="text-center">
                        <div className="w-4 h-4 bg-blue-500 rounded-full mx-auto mb-1"></div>
                        <p className="text-sm text-gray-600">Guide</p>
                        <p className="font-semibold">{chatOverview.modeDistribution.guide}</p>
                      </div>
                      <div className="text-center">
                        <div className="w-4 h-4 bg-green-500 rounded-full mx-auto mb-1"></div>
                        <p className="text-sm text-gray-600">Agent</p>
                        <p className="font-semibold">{chatOverview.modeDistribution.agent}</p>
                      </div>
                    </div>
                    <div className="relative w-48 h-48 mx-auto">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-900">{chatOverview.totalMessages}</p>
                          <p className="text-sm text-gray-500">Total Messages</p>
                        </div>
                      </div>
                      <svg viewBox="0 0 36 36" className="w-full h-full">
                        {/* Guide segment (blue) */}
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          strokeDasharray={`${(chatOverview.modeDistribution.guide / chatOverview.totalMessages) * 100}, 100`}
                        />
                        {/* Agent segment (green) */}
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="3"
                          strokeDasharray={`${(chatOverview.modeDistribution.agent / chatOverview.totalMessages) * 100}, 100`}
                          strokeDashoffset={`-${(chatOverview.modeDistribution.guide / chatOverview.totalMessages) * 100}`}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sender Distribution */}
            {chatOverview && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Message Sender Distribution</h3>
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="flex justify-center space-x-8 mb-4">
                      <div className="text-center">
                        <div className="w-4 h-4 bg-indigo-500 rounded-full mx-auto mb-1"></div>
                        <p className="text-sm text-gray-600">User</p>
                        <p className="font-semibold">{chatOverview.senderDistribution.user}</p>
                      </div>
                      <div className="text-center">
                        <div className="w-4 h-4 bg-pink-500 rounded-full mx-auto mb-1"></div>
                        <p className="text-sm text-gray-600">Agent</p>
                        <p className="font-semibold">{chatOverview.senderDistribution.agent}</p>
                      </div>
                    </div>
                    <div className="relative w-48 h-48 mx-auto">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-900">{chatOverview.totalMessages}</p>
                          <p className="text-sm text-gray-500">Total Messages</p>
                        </div>
                      </div>
                      <svg viewBox="0 0 36 36" className="w-full h-full">
                        {/* User segment (indigo) */}
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#6366f1"
                          strokeWidth="3"
                          strokeDasharray={`${(chatOverview.senderDistribution.user / chatOverview.totalMessages) * 100}, 100`}
                        />
                        {/* Agent segment (pink) */}
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#ec4899"
                          strokeWidth="3"
                          strokeDasharray={`${(chatOverview.senderDistribution.agent / chatOverview.totalMessages) * 100}, 100`}
                          strokeDashoffset={`-${(chatOverview.senderDistribution.user / chatOverview.totalMessages) * 100}`}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error Types Chart */}
          {errorStats && errorStats.errorsByType && errorStats.errorsByType.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Error Types Distribution</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Error Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Count
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Percentage
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Visualization
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {errorStats.errorsByType.map((errorType, index) => {
                      const percentage = errorStats.totalErrors > 0 
                        ? ((errorType.count / errorStats.totalErrors) * 100).toFixed(1) 
                        : 0;
                      return (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {errorType._id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {errorType.count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {percentage}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div 
                                className="bg-red-600 h-2.5 rounded-full" 
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Feedback Overview Section */}
          {feedbackOverview && feedbackOverview.feedbackByAgent && feedbackOverview.feedbackByAgent.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Agent Feedback Overview</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Agent Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Comments
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Likes
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dislikes
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Satisfaction Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {feedbackOverview.feedbackByAgent.map((agent, index) => {
                      const satisfactionRate = agent.totalComments > 0 
                        ? ((agent.likes / agent.totalComments) * 100).toFixed(1) 
                        : 0;
                      return (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {getAgentName(agent.agentId)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {agent.totalComments}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="text-green-600 font-medium">{agent.likes}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="text-red-600 font-medium">{agent.dislikes}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                                <div 
                                  className="bg-green-600 h-2 rounded-full" 
                                  style={{ width: `${satisfactionRate}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600">{satisfactionRate}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* User Activity Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">User Activity</h3>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-2 sm:mt-0">
                <div className="flex space-x-2">
                  <input
                    type="date"
                    name="startDate"
                    value={dateRange.startDate}
                    onChange={handleDateChange}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <input
                    type="date"
                    name="endDate"
                    value={dateRange.endDate}
                    onChange={handleDateChange}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <select
                  name="interval"
                  value={dateRange.interval}
                  onChange={handleDateChange}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>
                <div className="flex space-x-2">
                  <button
                    onClick={handleApplyFilter}
                    className="px-3 py-2 bg-black text-white rounded-md text-sm hover:bg-gray-800"
                  >
                    Apply
                  </button>
                  <button
                    onClick={handleResetFilter}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {userActivity && (
              <div>
                <div className="mb-4">
                  <p className="text-sm text-gray-500">
                    Showing {formatInterval(userActivity.period.interval)} data from{' '}
                    {userActivity.period.startDate === 'today' ? 'Today' : formatDate(userActivity.period.startDate)} to{' '}
                    {userActivity.period.endDate === 'today' ? 'Today' : formatDate(userActivity.period.endDate)}
                  </p>
                </div>
                
                {userActivity.data.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Active Users
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Visualization
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {userActivity.data.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.date}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.users}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div 
                                  className="bg-blue-600 h-2.5 rounded-full" 
                                  style={{ width: `${(item.users / Math.max(...userActivity.data.map(d => d.users))) * 100}%` }}
                                ></div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No activity data</h3>
                    <p className="mt-1 text-sm text-gray-500">No user activity found for the selected period.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;